// EditAccessDrawer — the person's ONE editor (ADR-050 §2): "Edit access", the
// primary act — the page's seat, the peek's main button (UserActions). A SlideOver (DrawerForm, ADR-038) over the shared
// AccessBody — Role (Administrator · the role, the ⓘ beside it) over Scope (the
// three sections) — saved through the edit-access door (PATCH …/users/:id, the
// `scope` block riding only when the scope part changed: one transaction, one
// correlation; a role-only change leaves the scope alone, so a giver changes the
// role of a person whose scope they may not edit). The person's grants outside
// the viewer's reach are read as counts and kept by the save; an "All" the viewer
// may not grant is read, not edited. Kept mounted by its host (keyed by the
// person). The edit rides the membership's row stamp (ADR-084): the shell seeds
// from the person's read as it is at each open, the save carries the stamp and
// the scope only when it changed since the open, and a stale save shows the line
// with Load the latest (the person's detail re-read).
//
// ADR-052 §5 PROACTIVE surfacing: as the scope is reduced, a debounced preview
// (POST …/scope/preview) says which of the person's chains would lose backing.
// BLOCKED (an orphan the viewer can't clear) → an inline Alert + the save is
// refused. Clearable orphans → a warning, and the save carries the consent
// (`confirm_unwind`) for THAT draft only: the preview is held with the draft it
// answered; a draft never shown is previewed at the save and shown first. The
// server re-checks authoritatively (SCOPE_CHANGE_BLOCKED / _UNWINDS).

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { BackingImpact } from '@ems-app/schemas/accountability/responses';
import type { ProjectUserDetail } from '@ems-app/schemas/users/responses';
import { DrawerForm } from '../../components/patterns/forms/DrawerForm.js';
import { readLatest } from '../../components/patterns/forms/readLatest.js';
import { useFormShell } from '../../components/patterns/forms/useFormShell.js';
import { OwnRefusal } from '../../components/patterns/forms/useRefusalLine.js';
import { Alert } from '../../components/ui/Alert.js';
import { useEntitlements } from '../../hooks/useEntitlements.js';
import { useEditUser } from '../../hooks/useUserMutations.js';
import { fetchUser, usersKeys } from '../../hooks/useUsers.js';
import { apiClient } from '../../lib/apiClient.js';
import { AccessBody, useRoleOpensTeams, type AccessValues } from './AccessBody.js';
import { membershipBody, membershipFormSchema, type MembershipFormValues } from './access.js';
import { scopeFormFromRead, scopeOutsideFromRead, scopeSectionsOf } from './scope.js';
import { draftScopeWords } from './scopeWords.js';

export interface EditAccessDrawerProps {
  projectId: string;
  user: ProjectUserDetail;
  /** Show the Administrator switch (the actor is admin/sysadmin in this project). */
  canGrantAdmin: boolean;
  open: boolean;
  onClose: () => void;
}

const EMPTY_IMPACT: BackingImpact = { orphaned: [], blocked: false };
/** A draft's key — the sections as the write takes them; the preview is held with the draft it answered. */
const keyOf = (scope: MembershipFormValues['scope']): string => JSON.stringify(scopeSectionsOf(scope));

export function EditAccessDrawer({ projectId, user, canGrantAdmin, open, onClose }: EditAccessDrawerProps) {
  const { t } = useTranslation('users');
  const qc = useQueryClient();
  const edit = useEditUser(projectId);
  const name = `${user.first_name} ${user.last_name}`;
  const previewUrl = `/projects/${projectId}/users/${user.user_id}/scope/preview`;

  const [preview, setPreview] = useState<{ key: string; impact: BackingImpact } | null>(null);

  const shell = useFormShell<MembershipFormValues, ProjectUserDetail>({
    schema: membershipFormSchema,
    row: user,
    // The seed is the read as it is at each open — never while open (an edit in progress is the operator's).
    values: (r) => {
      const u = r ?? user;
      return { is_project_admin: u.is_project_admin, role_id: u.role_id, scope: scopeFormFromRead(u.scope) };
    },
    // The fresh person: a quiet read (a person gone meanwhile is said in the form), into the page's entry once it answers.
    reload: () => readLatest(qc, usersKeys.detail(projectId, user.user_id), () => fetchUser(projectId, user.user_id, true)),
    onSubmit: async (values, { stamp, changed }) => {
      const key = keyOf(values.scope);
      // The scope rides when it changed since the drawer opened — the diff against the OPENED values, never the live
      // read (ADR-084 §6) — and says something new (an "All" switched on keeps the picks beneath it in the form).
      const opened = shell.form.formState.defaultValues?.scope as MembershipFormValues['scope'] | undefined;
      const scopeDirty = !values.is_project_admin && 'scope' in changed && (opened === undefined || key !== keyOf(opened));
      let confirmUnwind = false;
      if (scopeDirty) {
        // The consent rides the disclosure of THIS draft: the preview held for it — else asked now, shown first.
        const held = preview?.key === key ? preview.impact : null;
        const impact = held ?? (await apiClient.post<BackingImpact>(previewUrl, scopeSectionsOf(values.scope))).data;
        if (!held) setPreview({ key, impact });
        if (impact.blocked) throw new OwnRefusal(t('scope.backing.blockedShort'));
        if (impact.orphaned.length > 0 && !held) throw new OwnRefusal(t('scope.backing.reviewFirst'));
        confirmUnwind = impact.orphaned.length > 0;
      }
      await edit.mutateAsync({ userId: user.user_id, body: { ...membershipBody(values, { withScope: scopeDirty, confirmUnwind }), row_version: stamp } });
    },
    onSuccess: onClose,
  });

  const { watch, setValue, formState } = shell.form;
  const value: AccessValues = { is_project_admin: watch('is_project_admin'), role_id: watch('role_id'), scope: watch('scope') };
  const onChange = (next: AccessValues) => {
    setValue('is_project_admin', next.is_project_admin, { shouldDirty: true, shouldValidate: true });
    setValue('role_id', next.role_id, { shouldDirty: true, shouldValidate: true });
    setValue('scope', next.scope, { shouldDirty: true, shouldValidate: true });
  };

  // The preview of the proposed scope (ADR-052 §5) — a non-admin member's only (an
  // admin's row has no scope to preview); debounced; setPreview runs only in the async
  // callback (never synchronously in the effect — react-hooks/set-state-in-effect).
  const scope = value.scope;
  // The Teams section and the footer's words follow the ROLE being saved (a team key or the team level — ADR-083 §4),
  // never the read's old role; the module flag is the module's.
  const fieldForce = useEntitlements(projectId).has('has_field_force');
  const teamsOpen = useRoleOpensTeams(projectId, { fieldForce, isAdmin: value.is_project_admin, roleId: value.role_id });
  const key = keyOf(scope);
  const previewable = open && !value.is_project_admin && !user.is_project_admin;
  useEffect(() => {
    if (!previewable) return;
    const ctrl = new AbortController();
    const id = setTimeout(() => {
      apiClient
        .post<BackingImpact>(previewUrl, scopeSectionsOf(scope), { signal: ctrl.signal })
        .then((r) => setPreview({ key: keyOf(scope), impact: r.data }))
        .catch(() => {
          /* advisory — a draft never shown is previewed at the save */
        });
    }, 300);
    return () => {
      clearTimeout(id);
      ctrl.abort();
    };
  }, [previewable, scope, previewUrl]);
  // Only the preview of the draft on screen shows — never a stale one.
  const shownImpact = previewable && preview?.key === key ? preview.impact : EMPTY_IMPACT;

  return (
    <DrawerForm shell={shell} open={open} onClose={onClose} title={t('access.edit')} description={name} submitLabel={t('access.save')}>
      <div className="flex flex-col gap-4">
        {shownImpact.orphaned.length > 0 && (
          <Alert variant={shownImpact.blocked ? 'danger' : 'warning'}>
            <p className="font-medium">{shownImpact.blocked ? t('scope.backing.blockedTitle', { name }) : t('scope.backing.unwindTitle', { name })}</p>
            <ul className="mt-1 list-disc ps-5 text-sm">
              {shownImpact.orphaned.map((o) => (
                <li key={`${o.responsibility_id}-${o.scope_id}`}>{`${o.responsibility_name} · ${o.scope_label}`}</li>
              ))}
            </ul>
            <p className="mt-1 text-sm">{shownImpact.blocked ? t('scope.backing.blockedHelp') : t('scope.backing.unwindHelp')}</p>
          </Alert>
        )}
        <AccessBody
          projectId={projectId}
          value={value}
          onChange={onChange}
          canGrantAdmin={canGrantAdmin}
          roleError={formState.errors.role_id ? t('access.roleRequired') : undefined}
          giver={user.scope.giver}
          outside={scopeOutsideFromRead(user.scope)}
          vehicles={user.scope.vehicle_pools !== null}
          fieldForce={fieldForce}
        />
        {/* The footer says what the person will see — the summary's words (ADR-050 §3). */}
        {!value.is_project_admin && (
          <p className="text-sm text-[var(--color-text-secondary)]" role="status">
            <span className="font-medium text-[var(--color-text-primary)]">{t('scope.willSee')}</span>{' '}
            {draftScopeWords(t, scope, { vehicles: user.scope.vehicle_pools !== null, teams: teamsOpen, outside: scopeOutsideFromRead(user.scope) })}
          </p>
        )}
      </div>
    </DrawerForm>
  );
}
